import { ORCHESTRATION_STATUSES, STEP_STATUSES } from 'utils/common-constants';
import { IUpdateOrchestrationStatus } from 'interfaces/dashboard/dashboardSlice.interface';
import { IOrchestrationRow } from 'interfaces/dashboard/orchestrationRow.interface';
import {
  calculateJobDurationIfApplies,
  combineStatus,
  getNameFromEmail,
  updateSteps,
} from 'utils/commonFunctions/CommonFunctions';
import {
  IOrchestrationServer,
  IStepInformation,
} from 'interfaces/orchestration.interface';

export const mapResponseJobsToTableData = (
  jobs: IOrchestrationServer[]
): IOrchestrationRow[] => jobs.map((job) => mapJobToTableData(job));

export const mapJobToTableData = (
  job: IOrchestrationServer
): IOrchestrationRow => {
  return {
    id: job.jobId,
    name: job.workflow?.name || '',
    originalFileName: job.originalFileName || '',
    submitTime: job.submitTime as string,
    originalStartTime: (job.startTime ?? job.submitTime) as string,
    originalEndTime: job.endTime as string,
    startTime: (job.startTime as string) ?? '',
    endTime:
      job.status !== ORCHESTRATION_STATUSES.PENDING
        ? (job.endTime as string)
        : '',
    duration: calculateJobDurationIfApplies(job),
    originalStatus: job.status,
    status: job.status
      ? combineStatus(job.status)
      : ORCHESTRATION_STATUSES.PENDING,
    originalUser: job.user,
    user: {
      name: getNameFromEmail(job.user?.email) ?? 'Automated',
      id: job.user?.id,
      email: job.user?.email,
    },
    supportResubmit: supportResubmit(job),
    step: calculateStep(job),
    steps: job.steps,
    stepsCount: job.stepsCount,
    workflowId: job.workflow?.workflowId,
    product: job.product ?? undefined,
    children: job.children?.map((jobInstance) =>
      mapJobToTableData(jobInstance)
    ),
    path: job.path,
  };
};

export const updateJobStatusInTable = (
  job: IOrchestrationRow,
  updatedData: IUpdateOrchestrationStatus
): IOrchestrationRow => {
  const partialUpdatedJob = {
    ...job,
    originalStatus: updatedData.status,
    status: combineStatus(updatedData.status),
    startTime: updatedData.startTime ?? job.startTime,
    endTime: updatedData.endTime ?? job.endTime,
    supportResubmit: supportResubmit(job),
  };
  return {
    ...partialUpdatedJob,
    duration:
      partialUpdatedJob.startTime && !partialUpdatedJob.endTime
        ? job.duration
        : calculateJobDurationIfApplies(partialUpdatedJob),
    step: calculateStep(partialUpdatedJob),
  };
};

export const updateJobStepsInTable = (
  job: IOrchestrationRow,
  updatedData: IStepInformation
) => {
  const updatedJob = updateSteps(job, updatedData);
  let updatedSteps = updatedJob.steps || [];

  if (updatedSteps?.length) {
    if (
      !updatedSteps.some((step) => step.jobStepId === updatedData.jobStepId)
    ) {
      updatedSteps.push(updatedData);
    }
  } else {
    updatedSteps = [updatedData];
  }

  return {
    ...updatedJob,
    step: calculateStep(
      { ...updatedJob, steps: updatedSteps },
      updatedData.jobStepId
    ),
    steps: updatedSteps,
  };
};

const supportResubmit = (job: any | IOrchestrationRow): boolean => {
  const statuses = [
    ORCHESTRATION_STATUSES.FAILED,
    ORCHESTRATION_STATUSES.FINISHED_ERROR,
  ];

  return (
    statuses.includes(job.status) &&
    ((job.workflow?.supportResubmit && job.masterJobId != null) ||
      job.supportResubmit === true)
  );
};

const calculateStep = (job: any, currentStep?: number): string => {
  const preferOriginal =
    job?.originalStatus === ORCHESTRATION_STATUSES.STOPPING ||
    job?.originalStatus === ORCHESTRATION_STATUSES.FINISHED_ERROR;
  const currentStatus = preferOriginal ? job.originalStatus : job.status;
  if (currentStatus === ORCHESTRATION_STATUSES.PENDING) {
    return '';
  } else if (currentStatus === ORCHESTRATION_STATUSES.STOPPING) {
    return 'Stopping';
  } else if (currentStatus === ORCHESTRATION_STATUSES.STOPPED) {
    return 'Stopped';
  } else if (currentStatus === ORCHESTRATION_STATUSES.FINISHED) {
    return 'Finished';
  } else if (currentStatus === ORCHESTRATION_STATUSES.FINISHED_ERROR) {
    return 'Finished with errors';
  } else if (currentStatus === ORCHESTRATION_STATUSES.FAILED) {
    return 'Failed';
  } else {
    return calculateStepNumberIfInProgress(job);
  }
};

export const calculateStepNumberIfInProgress = (
  job: IOrchestrationServer | IOrchestrationRow
): string => {
  const { steps, stepsCount } = job;
  let stepsCountToUse = stepsCount;
  if (!stepsCountToUse) {
    stepsCountToUse = steps?.length;
  }
  if (
    !steps ||
    steps.length === 0 ||
    steps[0]?.status === STEP_STATUSES.NOT_STARTED
  ) {
    return `Step 1 of ${stepsCountToUse}`;
  }

  try {
    const lastStepIndex = steps[steps.length - 1]?.index;

    const inProgressStepsIndices = steps
      .filter((step) => step.status === STEP_STATUSES.IN_PROGRESS)
      .map((step) => step.index)
      .filter((index) => index != null && index !== undefined && index !== '');

    const uniqueInProgressIndices = Array.from(new Set(inProgressStepsIndices));

    if (uniqueInProgressIndices.length > 0) {
      const stepText = uniqueInProgressIndices.length === 1 ? 'Step' : 'Steps';
      return `${stepText} ${uniqueInProgressIndices.join(
        ', '
      )} of ${stepsCountToUse}`;
    }

    return lastStepIndex
      ? `Step ${lastStepIndex} of ${stepsCountToUse}`
      : (job as IOrchestrationRow).step ?? 'Loading...';
  } catch (error) {
    console.error('Error calculating step number:', error);
    return `Step 1 of ${stepsCountToUse || '...'}`;
  }
};
